import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'

/**
 * The Energy Industry Focus page — its copy and its figures, apart from the
 * component that draws them.
 *
 * **Why this cut has a page of its own.** `focus()` in `content/reports.ts` writes
 * every Industry Focus entry from one sentence template, and Energy was one of the
 * five still carrying it: a two-paragraph series description under an amber note saying
 * the sector's own words had never been supplied. They were supplied on 2026-08-28 —
 * *"referans alarak ilgili sayfayı birebir tıpa tıp aynısını tasarla"*, the same words
 * the Insurance, Logistics, Manufacturing, Pharmaceuticals, Public Services, Retail,
 * Services, Technology and Tourism cuts arrived in — with a four-part full-page capture
 * of digitopia.co's Energy Industry Focus page as the reference, plus four files: the
 * report mockup, Figure 1, and two overlapping crops of the cover. So this is the
 * thirty-first designed report page and the sixteenth focus cut to arrive with **both**
 * of its figures.
 *
 * **The shape is the reference's, band for band, and it is the folded focus shape
 * exactly**: hero → DAIMI Findings beside Figure 1 → the analysis strip with the
 * mockup → the dark methodology band → the global-perspective card → the download
 * close. So there is no component here at all, only this module and the dozen-line
 * adapter in `pages/DaimiEnergy.tsx`, which is what the fold in `pages/FocusPage.tsx`
 * was for.
 *
 * **Where the words come from, band by band.** Everything here is transcribed from the
 * reference capture. The *shape* of the page is certain; the *wording* is a careful
 * reading of body copy that renders a few pixels tall. Every band that carries prose
 * therefore carries a `check`, exactly as `content/daimiInsurance.ts` does, and
 * `daimiEnergyChecks()` feeds them into the same manifest as the rest of the site's
 * amber notes so a production build cannot ship them unread.
 *
 * **The one thing on this page that is decided rather than transcribed** is the closing
 * band's destination, and it was decided in the request: *"en aşağıda önceden
 * konuştuğumuz gibi 2024'e değil 2026 DAIMI report'a yönlendirelim"*. That band is
 * `content/reportClosing.ts`, shared by every report page but three, and it already
 * points at the Digital & AI Maturity Report 2026 — so this page inherits the answer
 * rather than restating it, and the reference capture agrees: its Global Perspective
 * card names the 2026 report too, and names this cut "The Energy Focus Report", which
 * is the `shortName` `focus()` already gives it.
 *
 * **What the live page has that this page cannot.** Its Download buttons open an
 * embedded HubSpot form — the fourth capture is that form, filled in. These documents
 * are prerendered with no client bundle at all (see `src/prerender.tsx`), so an
 * embedded form script would never run, and the other reports on this site hand the
 * reader the PDF from our own bucket instead. So every Download button here is that
 * PDF, and `DAIMI_EN_DOWNLOAD.check` is what keeps the question of gating open.
 *
 * **Three energy-adjacent reports, three pages, and it is easy to hang the wrong one
 * on the wrong page.** This is the Energy cut of the maturity study. The Future 2030
 * Energy & Utilities outlook is a separate report with its own slug, cover and designed
 * page (`content/outlookEnergy.ts`, `pages/OutlookEnergy.tsx`) — which is why the
 * constants here are `DAIMI_EN_*` and that page's are `OUTLOOK_EN_*` — and the
 * Utilities Industry Focus cut (`content/daimiUtilities.ts`) is a third report again.
 * `reports.ts` says the same thing about the PDF keys:
 * `…-daimi-2026-report-energy.pdf` is this cut, not the outlook and not Utilities.
 */

/**
 * The report this page belongs to — the branch `pages/Reports.tsx` takes on it.
 *
 * The slug is unchanged from the entry `reports.ts` has listed since the index was
 * built, and it is the one the request named: it is what digitopia.co serves, and
 * tidying it would break the inbound links this page exists to keep working.
 *
 * `content/outlookEnergy.ts` needs it too — its maturity band points here — and used to
 * hold its own copy of the literal with a note saying the two had to stay in step. It
 * imports this one now, because this module exists.
 */
export const DAIMI_EN_SLUG = 'digital-and-ai-maturity-report-2026-energy-industry'

/**
 * The hero's three paragraphs, read off the reference capture.
 *
 * Three, like the Insurance and Banking cuts and unlike Logistics: the third is
 * `FOCUS_CLOSE` in `reports.ts` word for word — the series' own closing line, which
 * the live Energy hero closes with too. Written out rather than imported so that
 * rewording the series line for the other cuts cannot silently reword the page it
 * appears on.
 *
 * The 2.88 and the 2.93 are set in bold on the live page. They are plain here, because
 * this page renders its paragraphs as strings rather than as markup — and the 2.88 is
 * the one number the page can check against itself: it is also the 2025 average in
 * Figure 1.
 */
export const DAIMI_EN_INTRO = {
  body: [
    'The energy sector scores 2.88 in the 2026 DAIMI benchmark, trailing the 2.93 cross-industry baseline as it balances safety-critical operations with the transition to clean energy.',
    'To lead in 2026, organizations must overcome “legacy gravity” and data fragmentation to modernize their core and industrialize AI foundations for long-term resilience.',
    'This report outlines the sector’s maturity, leading strategies, and actions to accelerate transformation.',
  ],
  /**
   * The report cover. It already existed as a 400px square — the size every card in the
   * reports grid carries — and the file attached with this request is the same artwork
   * much larger, so it replaced it under the same key rather than landing beside it
   * under a second name. The index card reads the same path and simply gets a sharper
   * cover out of it.
   *
   * **With the same caveat the Insurance and Logistics covers had, file for file.** It
   * arrived as two captures, "(1 of 2)" and "(2 of 2)", and they are not two halves —
   * they are overlapping crops of the same square artwork, one cut off at the bottom
   * through the logo and one carrying the whole design. The second is the one held here,
   * at 2000 × 1848 rather than square, so it is missing a sliver of the top margin, and
   * the index card's square crop trims about 4% off each side. `check` asks for the file
   * itself, which would replace this under the same key.
   */
  photo: {
    src: '/photo/daimi-report-2026-energy-cover.webp',
    alt: 'Digital & AI Maturity Report 2026: Energy Focus, February 2026',
  } satisfies Photo,
  check:
    'Three things. The three paragraphs are transcribed from the reference capture rather than supplied as text, so they want reading against the live page once before production — including the 2.88 and the 2.93, which the capture sets in bold and which are plain here. The 2.88 is Figure 1’s 2025 average too, so the page checks itself on that one; the 2.93 cross-industry baseline is the one figure on the page none of the supplied files can confirm, though it is the same baseline the Logistics cut prints, which is a point in its favour. Second, the live hero has no cover beside the words — the right of it is empty but for the ribbon — and the cover is there here because that is what every other designed report page on this site does, and because the 2000px file you supplied would otherwise only ever be seen as a 400px index card. Say if the live emptiness was the point. Third, the cover file itself is not whole: the two “scaled” captures you attached are overlapping crops of one square image, so the fuller one held here is 2000 × 1848 and loses a sliver of the margin above the title, with the index card trimming roughly 4% off each side to make it square again. Send the cover as a single file and it will replace this one under the same name.',
}

/**
 * The findings, and Figure 1 beside them.
 *
 * The live band is a lede paragraph followed by three lines that each open with a
 * bolded label — "Core Strengths:", "The Scaling Gap:", "Industrializing AI:". Split
 * into `label` and `body` here so the label can be set rather than emphasised inside a
 * sentence, which is what a `<strong>` in the middle of a paragraph gives.
 *
 * The heading is "DAIMI Findings" and nothing after it: like the Insurance and
 * Logistics cuts and unlike the Pharmaceuticals one, this page's live heading carries
 * no subtitle, so none is invented here.
 */
export const DAIMI_EN_FINDINGS = {
  heading: 'DAIMI Findings',
  lede: 'Energy performance shows a maturity distribution centered around mid-level performance, where operational stability often outpaces digital velocity.',
  points: [
    {
      label: 'Core Strengths',
      body: 'Maturity is highest in domain-specific analytics for asset performance, predictive maintenance, and grid monitoring.',
    },
    {
      label: 'The Scaling Gap',
      body: 'Progress is limited by inconsistent asset hierarchies and fragmented operational telemetry that reduce trust and slow enterprise-wide automation.',
    },
    {
      label: 'Industrializing AI',
      body: 'While many have advanced analytics in silos, leaders are those connecting these insights to end-to-end workflows in field operations and planning.',
    },
  ],
  /**
   * Figure 1 itself — the six-year study-results chart supplied with this request, the
   * same shape the Insurance, Logistics, Manufacturing, Automotive, Banking, Services,
   * Retail and Pharmaceuticals cuts carry rather than the vitality scatter the
   * Technology, Tourism and Public Services cuts do or the portrait score range the
   * Utilities cut does. Six years, 2020 to 2025, each one a range from the laggard
   * through the average to the leader with that year's company count in the header. It
   * is 1200 × 628 on a white ground, so it gets a white card and fills it whole.
   */
  photo: {
    src: '/photo/daimi-energy-study-results.webp',
    alt: 'DAIMI study results for the energy industry, 2020 to 2025: the laggard, average and leader score each year, from 2.10–2.60–2.80 in 2020 to 2.16–2.88–3.55 in 2025',
  } satisfies Photo,
  caption:
    'Figure 1: Digital & AI Maturity Index (DAIMI) Study Results: Energy Industry Score, Averages, Leaders, and Laggards',
  expertLabel: 'Talk to an expert',
  expertHref: LINKS.contact,
  check:
    'Figure 1 is the file you supplied and is shown whole, including its own year-by-year company counts (3 in 2020 rising to 76 in 2025) — the live caption carries neither those nor a year, so they are stated in the alt text and nowhere else. Its 2025 average, 2.88, is the number the hero opens with, which is the one cross-check the page can do on itself. Two readings the chart supports and the prose does not mention: the spread is widening rather than the middle rising — the leader has climbed steadily from 2.80 to 3.55 while the average has moved only from 2.60 to 2.88 and sat flat between 2024 and 2025 — and the laggard line went backwards, 2.40 in 2024 down to 2.16 in 2025, which is below where it stood in 2022. That is a sharper picture of “operational stability outpacing digital velocity” than the lede gives, and 2025 is the widest leader-to-laggard gap in the six years. What wants a reading against digitopia.co is the prose: the lede and the three findings are transcribed from the capture, where body copy renders a few pixels tall.',
}

/** The analysis band, with the report mockup beside it. */
export const DAIMI_EN_ANALYSIS = {
  heading: 'A Comprehensive Analysis of Digital & AI Maturity in Energy',
  body: [
    'To win in 2026, energy executives must move beyond site-based initiatives to enterprise programs tied to measurable value. Success requires a disciplined focus on modernizing the OT/IT integration and upskilling field teams to turn operational data into strategic agility.',
    'Download the full report to access the detailed DAIMI benchmarks and the strategic roadmap to bridge the gap between isolated excellence and enterprise maturity.',
  ],
  /**
   * The mockup supplied with this request — the Energy Focus cover, not the Future 2030
   * Energy & Utilities outlook's, which this site already holds separately as
   * `/photo/future-2030-outlook-energy-mockup.webp`. Unlike most of the other cuts, the
   * reference capture *does* show it here: the live analysis band carries the two
   * overlapping copies on the right, which is exactly what this band draws.
   */
  photo: {
    src: '/photo/daimi-report-2026-energy-mockup.webp',
    alt: 'Two copies of the Digital & AI Maturity Report 2026 Energy Focus',
  } satisfies Photo,
  check:
    'The paragraphs are transcribed from the reference capture rather than supplied as text, so they want reading against the live page once before production — the first one in particular, which is the page’s argument in one sentence. The mockup beside them is not an addition on this cut: the live analysis band carries it, in the same place and at the same size, so that column is a transcription like the words. It is the smallest of the three files at 400 × 326, which is why it is capped at `max-w-sm` and cannot grow past it without showing soft — send it larger if this column should carry more weight.',
}

/*
 * The methodology and global-perspective bands are not written out here. They are
 * `content/reportClosing.ts`, drawn by `components/ReportClosing.tsx` on every report
 * page but three — and the reference capture for this page shows exactly that pair,
 * pointing at the Digital & AI Maturity Report 2026, which is the "2024 değil 2026" the
 * request asked for. Nothing to change: the shared band already links to `DAIMI_SLUG`,
 * and its opening sentence already names this cut "The Energy Focus Report" — the
 * capture says "The Energy Focus Report" too, which is the `shortName` `focus()` mints.
 */

/** The closing band, which is where the live page's gated form sits. */
export const DAIMI_EN_DOWNLOAD = {
  heading: 'Download Report',
  body: 'Get the Energy Industry Focus cut of the Digital & AI Maturity Report 2026.',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy checks
 * in — the same mechanism `daimiChecks()` and `daimiInsuranceChecks()` use, and
 * appended to the same list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file — this cut arrived
 * with both of its figures and its cover. Three of the notes are a transcription nobody
 * has read against the original, and one is a decision about gating that belongs to
 * marketing.
 */
export function daimiEnergyChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: DAIMI_EN_INTRO.check },
    { section: 'findings', check: DAIMI_EN_FINDINGS.check },
    { section: 'analysis', check: DAIMI_EN_ANALYSIS.check },
    { section: 'download', check: DAIMI_EN_DOWNLOAD.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${DAIMI_EN_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
